'use client';

import React from 'react';
import { CheckCircle2, FileText, Package } from 'lucide-react';
import type { EntryFieldConfig, EntryTypeConfig } from './expense';
import type { VehicleInfo } from './expense';
import type { ChildEntryType } from '../types';
import type { UseOdometerCaptureReturn } from '../hooks/useOdometerCapture';
import SmartOdometerCapture from './SmartOdometerCapture';
import { ExpenseLineItemsEditor } from './expense/ExpenseLineItemsEditor';
import { EXPENSE_ENTRY_TYPES } from './expense';
import type { DetectDuplicateLineItemsResult } from './expense/lineItemDedup';
import type { DocumentLineItem } from '@/hooks/useDocumentsWorkItems';
import type { PoFulfillment } from './expense/useProcurementSearch';
import type { DocumentTaxRate } from '@/components/finance/modals/SortableLineItem';
import type { ExpenseAccountOption } from '@/lib/finance/expenseAccountMapping';
import type { CaptureCapabilities } from './expense/captureSchema';
import DateTimePicker from '@/components/shared/DateTimePicker';
import { SegmentedDecimalInput } from './SegmentedDecimalInput';

export interface DisplayAccountCode {
  code: string;
  name: string | null;
}

export interface FormFieldsSectionProps {
  selectedEntryType: ChildEntryType;
  config: EntryTypeConfig;
  formData: Record<string, string>;
  updateField: (key: string, value: string) => void;
  handleAmountChange: (value: string) => void;
  isExpenseType: boolean;
  needsOdometer: boolean;
  vehicleInfo: VehicleInfo | null;
  odometerCapture: UseOdometerCaptureReturn;
  submitting: boolean;
  accent: string;
  detectedCurrency: string;
  isDark: boolean;
  // Line items
  expenseLineItems: DocumentLineItem[];
  setExpenseLineItems: React.Dispatch<React.SetStateAction<DocumentLineItem[]>>;
  showAddLineItem: boolean;
  setShowAddLineItem: React.Dispatch<React.SetStateAction<boolean>>;
  newLineItem: { description: string; quantity: number; unit_price: number; tax_rate_id: number | undefined };
  setNewLineItem: React.Dispatch<React.SetStateAction<{ description: string; quantity: number; unit_price: number; tax_rate_id: number | undefined }>>;
  taxRates: DocumentTaxRate[];
  preferredDefaultTaxRate: DocumentTaxRate | null;
  expenseAccounts: ExpenseAccountOption[];
  capabilities: CaptureCapabilities;
  isJobLinkedExpense: boolean;
  // PO fulfillment for "Load from PO" button
  poFulfillment: PoFulfillment | null;
  // Account code display
  displayAccountCode: DisplayAccountCode | null;
  // Intra-receipt duplicate line items (overlapping multi-photo capture)
  lineItemDuplicates?: DetectDuplicateLineItemsResult;
  onRemoveDuplicateLineItems?: () => void;
  onDismissLineItemDuplicates?: () => void;
}

export function FormFieldsSection(props: FormFieldsSectionProps) {
  const {
    selectedEntryType, config, formData, updateField, handleAmountChange,
    isExpenseType, needsOdometer, vehicleInfo, odometerCapture, submitting, accent,
    detectedCurrency, isDark,
    expenseLineItems, setExpenseLineItems, showAddLineItem, setShowAddLineItem,
    newLineItem, setNewLineItem, taxRates, preferredDefaultTaxRate,
    expenseAccounts, capabilities, isJobLinkedExpense,
    poFulfillment, displayAccountCode,
    lineItemDuplicates, onRemoveDuplicateLineItems, onDismissLineItemDuplicates,
  } = props;

  return (
    <>
      {/* ── Expense Line Items (all expense types) ── */}
      {EXPENSE_ENTRY_TYPES.includes(selectedEntryType) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            {selectedEntryType === 'delivery_note' ? 'Delivered Items *' : 'Line Items'}
          </label>

          {/* Field purchases provisionally stock the capturer's van — tell the
              technician so they know the items become issuable immediately
              (and get pulled back out if finance rejects the document). */}
          {isJobLinkedExpense &&
            ['purchase', 'delivery_note', 'grn', 'supplier_invoice'].includes(selectedEntryType) && (
            <p className="mb-1.5 flex items-start gap-1.5 text-xs text-blue-600 dark:text-blue-400">
              <Package className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Stocked items are added to your van right away so you can issue them to jobs — they stay provisional until finance approves.
            </p>
          )}

          <ExpenseLineItemsEditor
            expenseLineItems={expenseLineItems}
            setExpenseLineItems={setExpenseLineItems}
            showAddLineItem={showAddLineItem}
            setShowAddLineItem={setShowAddLineItem}
            newLineItem={newLineItem}
            setNewLineItem={setNewLineItem}
            taxRates={taxRates}
            preferredDefaultTaxRate={preferredDefaultTaxRate ?? null}
            accent={isDark ? '#3b82f6' : '#2563eb'}
            expenseAccounts={expenseAccounts}
            capabilities={capabilities}
            lineItemDuplicates={lineItemDuplicates}
            onRemoveDuplicates={onRemoveDuplicateLineItems}
            onDismissDuplicates={onDismissLineItemDuplicates}
          />

          {/* Load from PO button */}
          {poFulfillment && poFulfillment.lineItems.some((line) => line.remainingQty > 0) && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => {
                  const remainingItems: DocumentLineItem[] = poFulfillment.lineItems
                    .filter((line) => line.remainingQty > 0)
                    .map((line, idx) => ({
                      description: line.description,
                      quantity: line.remainingQty,
                      unit_price: line.unit_price || 0,
                      line_type: 'item' as const,
                      sort_order: idx + 1,
                      unit: 'each',
                      tax_rate: 0,
                      tax_amount: 0,
                    }));
                  setExpenseLineItems(remainingItems);
                }}
                className="w-full rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 py-2 text-sm text-blue-600 hover:border-blue-400 hover:text-blue-700 transition-colors dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:border-blue-500 dark:hover:text-blue-300"
              >
                <Package className="h-4 w-4 inline mr-1" />
                Load from PO
              </button>
            </div>
          )}
        </div>
      )}

      {/* Resolved expense account code display */}
      {displayAccountCode && capabilities.canAllocateGl && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/50">
          <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Account</span>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
              {displayAccountCode.code} — {displayAccountCode.name || 'Unmapped'}
            </p>
          </div>
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">
          Details
        </label>
        <div className="space-y-3">
          {config.fields.map((field) => {
            const isFuelTypeLockedFromVehicle =
              selectedEntryType === 'fuel' && field.key === 'fuel_type' && vehicleInfo?.fuelType;

            return (
              <div key={field.key}>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {field.label} {(field.required || (needsOdometer && field.key === 'odometer')) && <span className="text-red-500">*</span>}
                </label>

                {isFuelTypeLockedFromVehicle ? (
                  <div className="w-full px-3 py-2.5 rounded-xl border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                      {vehicleInfo!.fuelType!.replace(/_/g, ' ')}
                    </span>
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-green-600 dark:text-green-400">
                      <CheckCircle2 className="w-3 h-3" />
                      Vehicle setting
                    </span>
                  </div>
                ) : (field.key === 'odometer' && needsOdometer) ? (
                  <SmartOdometerCapture
                    capture={odometerCapture}
                    label=""
                    required={field.required || (needsOdometer && field.key === 'odometer')}
                    disabled={submitting}
                    accentColor={accent}
                    compact
                    preferNativeCameraOnCapacitor={false}
                  />
                ) : field.type === 'datetime' ? (
                  // Stored as `YYYY-MM-DDTHH:mm`, or bare `YYYY-MM-DD` while
                  // the time is still missing — the slip time is required for
                  // reliable duplicate detection, so an empty time blocks
                  // submission (see requiredComplete in ChildEntryForm).
                  // Picker-only editing (calendar + time dropdown) — no
                  // free-text entry — so corrections always produce a valid,
                  // complete datetime.
                  (() => {
                    const rawValue = String(formData[field.key] || '');
                    const hasTime = /T\d{2}:\d{2}/.test(rawValue);
                    return (
                      <>
                        <DateTimePicker
                          value={hasTime ? rawValue : null}
                          onChange={(v) => updateField(field.key, v)}
                          showTime
                          disabled={submitting}
                          isDark={isDark}
                          placeholder="Select slip date & time…"
                        />
                        {!hasTime && (
                          <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                            Required — pick the slip date &amp; time
                          </p>
                        )}
                      </>
                    );
                  })()
                ) : field.type === 'date' ? (
                  <input
                    type="date"
                    value={formData[field.key] || ''}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:outline-none"
                  />
                ) : field.type === 'select' ? (
                  <select
                    value={formData[field.key] || ''}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:outline-none"
                  >
                    <option value="">Select...</option>
                    {field.options?.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={formData[field.key] || ''}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    rows={3}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white resize-none focus:ring-2 focus:outline-none"
                  />
                ) : field.key === 'litres' ? (
                  // Fuel litres are captured as XXX,XX — MFA-style digit
                  // blocks (3 whole + 2 fraction) since slips always show
                  // two decimals and the value must be > 0 to save.
                  <SegmentedDecimalInput
                    value={formData[field.key] || ''}
                    onChange={(v) => updateField(field.key, v)}
                    disabled={submitting}
                    suffix="L"
                  />
                ) : field.type === 'number' ? (
                  <div className="relative">
                    {field.isCurrency && (
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400 pointer-events-none select-none">
                        {detectedCurrency === 'ZAR' ? 'R' : detectedCurrency}
                      </span>
                    )}
                    <input
                      type="number"
                      inputMode="decimal"
                      step={field.isCurrency ? '0.01' : '1'}
                      value={formData[field.key] || ''}
                      onChange={(e) => {
                        if (field.key === 'amount' && isExpenseType) {
                          handleAmountChange(e.target.value);
                        } else {
                          updateField(field.key, e.target.value);
                        }
                      }}
                      placeholder={field.placeholder}
                      className={`w-full ${field.isCurrency ? '!pl-9' : '!pl-3'} !pr-3 !py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:outline-none`}
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData[field.key] || ''}
                    onChange={(e) => updateField(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:outline-none"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
